import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useDispatch } from 'react-redux';
import { Box, IconButton, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { ContentCopy, Visibility, VisibilityOff } from '@mui/icons-material';
import MarkdownDisplay from './MarkdownDisplay';
import { contentToPlainText } from '../utils/lexicalContent';
import { sanitizeUrl, splitUrlCredentials, withUrlCredentials } from '../utils/navigateUtils';
import { showAlert } from '../redux/slices/alerts';

const text = (value) => (typeof value === 'string' ? value.trim() : '');

const hasRichText = (value) => Boolean(value) && contentToPlainText(value) !== '';

export const contextHref = (value) => {
    const trimmed = text(value);
    if (!trimmed || /\s/.test(trimmed)) return null;
    if (/^[a-z][a-z0-9+.-]*:(?!\d)/i.test(trimmed)) {
        const safe = sanitizeUrl(trimmed);
        return safe === 'about:blank' || !/^https?:\/\//i.test(safe) ? null : safe;
    }
    return /^[\w-]+(\.[\w-]+)+(:\d+)?(\/\S*)?$/.test(trimmed) ? `https://${trimmed}` : null;
};

const environmentRows = (context) =>
    Array.isArray(context?.environments)
        ? context.environments.filter((env) => text(env?.name) || text(env?.url) || text(env?.vaultRef) || text(env?.branch) || text(env?.username) || text(env?.password))
        : [];

export const hasHumanContext = (context) =>
    Boolean(
        context &&
            (text(context.site) ||
                text(context.host?.provider) ||
                text(context.host?.dashboardUrl) ||
                text(context.repository?.url) ||
                environmentRows(context).length ||
                text(context.designsUrl) ||
                text(context.qaBoard?.type) ||
                text(context.qaBoard?.url) ||
                hasRichText(context.testInstructions) ||
                hasRichText(context.notes)),
    );

const Details = styled(Box, { shouldForwardProp: (prop) => prop !== 'compact' })(({ theme, compact }) => ({
    display: 'grid',
    gridTemplateColumns: 'minmax(96px, max-content) minmax(0, 1fr)',
    columnGap: theme.spacing(2),
    rowGap: theme.spacing(compact ? 0.75 : 1.25),
    alignItems: 'baseline',
    [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: 'minmax(0, 1fr)',
        rowGap: theme.spacing(0.25),
    },
}));

const Term = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.secondary,
    [theme.breakpoints.down('sm')]: {
        marginTop: theme.spacing(0.75),
    },
}));

const Value = styled(Typography)({
    minWidth: 0,
    overflowWrap: 'anywhere',
});

const Separator = styled('span')(({ theme }) => ({
    color: theme.palette.text.secondary,
    margin: theme.spacing(0, 0.75),
}));

const Block = styled(Box, { shouldForwardProp: (prop) => prop !== 'compact' })(({ theme, compact }) => ({
    marginTop: theme.spacing(compact ? 1.5 : 2),
}));

const BlockTitle = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
}));

const EnvTableContainer = styled(TableContainer)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflowX: 'auto',
}));

const WrapCell = styled(TableCell)({
    overflowWrap: 'anywhere',
    minWidth: 96,
});

const AccessLine = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    columnGap: 2,
    minWidth: 0,
});

const AccessLabel = styled('span')(({ theme }) => ({
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(0.5),
}));

const AccessValue = styled('span')({
    minWidth: 0,
    overflowWrap: 'anywhere',
});

const AccessButton = styled(IconButton)({
    padding: 2,
});

const AccessIcon = styled(ContentCopy)({
    fontSize: 16,
});

const RevealOnIcon = styled(Visibility)({
    fontSize: 16,
});

const RevealOffIcon = styled(VisibilityOff)({
    fontSize: 16,
});

const CopyButton = ({ value, what }) => {
    const dispatch = useDispatch();
    const label = what === what.toUpperCase() ? what : what.toLowerCase();
    const handleCopy = () => {
        const failed = () => dispatch(showAlert({ severity: 'error', message: `Could not copy the ${label}` }));
        if (!navigator.clipboard?.writeText) return failed();
        navigator.clipboard.writeText(value).then(() => dispatch(showAlert({ severity: 'success', message: `${what} copied to clipboard` })), failed);
    };
    return (
        <Tooltip title={`Copy ${label}`}>
            <AccessButton size="small" aria-label={`Copy ${label}`} onClick={handleCopy}>
                <AccessIcon />
            </AccessButton>
        </Tooltip>
    );
};

const EnvironmentAccess = ({ env }) => {
    const [revealed, setRevealed] = useState(false);
    const parsed = splitUrlCredentials(text(env.url));
    const url = text(parsed.url);
    const username = text(env.username) || parsed.username;
    const password = text(env.password) || parsed.password;
    if (!url && !username && !password) return '—';
    const href = url ? contextHref(url) : null;
    const handleOpen = (event) => {
        if (!username && !password) return;
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        window.open(withUrlCredentials(href, username, password), '_blank', 'noopener,noreferrer');
    };
    return (
        <>
            {url && (
                <AccessLine>
                    <AccessValue>
                        {href ? (
                            <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover" onClick={handleOpen}>
                                {url}
                            </Link>
                        ) : (
                            url
                        )}
                    </AccessValue>
                    <CopyButton value={url} what="URL" />
                </AccessLine>
            )}
            {username && (
                <AccessLine>
                    <AccessLabel>User</AccessLabel>
                    <AccessValue>{username}</AccessValue>
                    <CopyButton value={username} what="Username" />
                </AccessLine>
            )}
            {password && (
                <AccessLine>
                    <AccessLabel>Password</AccessLabel>
                    <AccessValue>{revealed ? password : '••••••••'}</AccessValue>
                    <Tooltip title={revealed ? 'Hide password' : 'Show password'}>
                        <AccessButton size="small" aria-label={revealed ? 'Hide password' : 'Show password'} onClick={() => setRevealed((prev) => !prev)}>
                            {revealed ? <RevealOffIcon /> : <RevealOnIcon />}
                        </AccessButton>
                    </Tooltip>
                    <CopyButton value={password} what="Password" />
                </AccessLine>
            )}
        </>
    );
};

const ContextLink = ({ value }) => {
    const shown = text(value);
    if (!shown) return null;
    const href = contextHref(shown);
    return href ? (
        <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover">
            {shown}
        </Link>
    ) : (
        shown
    );
};

const Row = ({ term, children }) => (
    <>
        <Term>{term}</Term>
        <Value variant="body2" component="div">
            {children}
        </Value>
    </>
);

const TagContextDetails = ({ context, compact = false, emptyText }) => {
    if (!hasHumanContext(context)) {
        return emptyText ? (
            <Typography variant="body2" color="text.secondary">
                {emptyText}
            </Typography>
        ) : null;
    }
    const provider = text(context.host?.provider);
    const dashboardUrl = text(context.host?.dashboardUrl);
    const qaType = text(context.qaBoard?.type);
    const qaUrl = text(context.qaBoard?.url);
    const environments = environmentRows(context);

    return (
        <Box>
            <Details compact={compact}>
                {text(context.site) && (
                    <Row term="Site">
                        <ContextLink value={context.site} />
                    </Row>
                )}
                {(provider || dashboardUrl) && (
                    <Row term="Host">
                        {provider}
                        {provider && dashboardUrl && <Separator>·</Separator>}
                        <ContextLink value={dashboardUrl} />
                    </Row>
                )}
                {text(context.repository?.url) && (
                    <Row term="Repository">
                        <ContextLink value={context.repository.url} />
                    </Row>
                )}
                {text(context.designsUrl) && (
                    <Row term="Designs">
                        <ContextLink value={context.designsUrl} />
                    </Row>
                )}
                {(qaType || qaUrl) && (
                    <Row term="QA board">
                        {qaType}
                        {qaType && qaUrl && <Separator>·</Separator>}
                        <ContextLink value={qaUrl} />
                    </Row>
                )}
            </Details>

            {environments.length > 0 && (
                <Block compact={compact}>
                    <BlockTitle>Environments</BlockTitle>
                    <EnvTableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>URL and login</TableCell>
                                    <TableCell>Vault reference</TableCell>
                                    <TableCell>Branch</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {environments.map((env, index) => (
                                    <TableRow key={`${text(env.name)}-${index}`}>
                                        <WrapCell>{text(env.name) || '—'}</WrapCell>
                                        <WrapCell>
                                            <EnvironmentAccess env={env} />
                                        </WrapCell>
                                        <WrapCell>{text(env.vaultRef) ? <ContextLink value={env.vaultRef} /> : '—'}</WrapCell>
                                        <WrapCell>{text(env.branch) || '—'}</WrapCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </EnvTableContainer>
                </Block>
            )}

            {hasRichText(context.testInstructions) && (
                <Block compact={compact}>
                    <BlockTitle>Test instructions</BlockTitle>
                    <MarkdownDisplay content={context.testInstructions} />
                </Block>
            )}

            {hasRichText(context.notes) && (
                <Block compact={compact}>
                    <BlockTitle>Notes</BlockTitle>
                    <MarkdownDisplay content={context.notes} />
                </Block>
            )}
        </Box>
    );
};

export default TagContextDetails;
